import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import connect from '~/store/connect';
import { t } from '~/i18n';
import { DashboardLayout, DropdownOption, MenuRoute, Modal, Select } from '@ixicare/ui';
import { State } from '~store/types';
import SoundService from '~services/SoundService';
import NotificationService from '~services/NotificationService';
import { ProfileType, HcpGroup } from '@ixicare/resources';
import { observer } from 'mobx-react';
import { useStores } from '~store/utils';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useSiteConfiguration } from '~containers/ConfigurationForm/ConfigurationForm';

interface DashboardWrapperProps {
  children: React.ReactNode;
  routes: MenuRoute[];
}

const DashboardWrapper: React.FC<DashboardWrapperProps> = observer(({ children, routes }) => {
  const navigate = useNavigate();
  const { auth } = useStores();

  const dropDownEntries: DropdownOption[] = [
    {
      label: t('user.actions.logout'),
      icon: 'log-out',
      onClick: auth.logout,
    },
    {
      label: t('user.actions.view'),
      onClick: () => auth.user && navigate(`/healthcare-providers/${auth.user.id}/edit`),
      icon: 'user',
    },
  ];

  // Fetch HCP groups
  const { data: groups } = useQuery({
    queryKey: ['hcpGroupsAll'],
    queryFn: () => HcpGroup.getHcpGroups(),
  });

  // Mutation to join/switch group
  const joinGroupMutation = useMutation({
    mutationFn: ({ groupId, hcpId }: { groupId: string; hcpId: number }) =>
      HcpGroup.assignHcpToHcpGroup(groupId, hcpId),
    onSuccess: () => {
      // Refresh user data to get updated hcpGroupId
      if (auth.user?.id) {
        auth.fetchUser(auth.user.id, () => {});
      }
    },
    onError: (error) => {
      console.error('Error switching group:', error);
    },
  });

  const handleGroupChange = (groupId: string) => {
    if (auth.user) {
      joinGroupMutation.mutate({ groupId, hcpId: auth.user.id });
    }
  };

  // Check if segmentation is enabled
  const { data: siteConfig } = useSiteConfiguration();
  const isSelfAssignEnabled = siteConfig?.['enable-hcp-self-assign-hcp-group'] ?? false;

  // Prepare group options for Select component
  const groupOptions = React.useMemo(() => {
    if (!groups) return [];
    return groups.map((group) => ({
      value: group.id,
      label: group.hcpGroupName,
    }));
  }, [groups]);

  // Group selector component
  const groupSelector =
    auth.user?.profileType === ProfileType.HealthcareProvider && isSelfAssignEnabled && groups && groups.length > 0 ? (
      <>
        <div className="row" style={{ alignItems: 'center' }}>
          <div className="col-auto">
            <label style={{ whiteSpace: 'nowrap', marginBottom: 0 }}>{t('hcpGroups.currentGroup') + ':'}</label>
          </div>
          <div className="col">
            <Select
              value={auth.user?.hcpGroupId || null}
              onChange={handleGroupChange}
              options={groupOptions}
              placeholder={t('hcpGroups.select.placeholder')}
              as="filter"
            />
          </div>
        </div>
        {!auth.user?.hcpGroupId && (
          <Modal>
            <h3>{t('hcpGroups.currentGroup')}</h3>
            <p className="mb-20">{t('hcpGroups.modal.selectGroupPrompt')}</p>
            <div className="d-flex justify-content-end">
              <Select
                value={auth.user?.hcpGroupId || null}
                onChange={handleGroupChange}
                options={groupOptions}
                placeholder={t('hcpGroups.select.placeholder')}
                as="filter"
              />
            </div>
          </Modal>
        )}
      </>
    ) : null;

  const ConnectedDashboard = React.useMemo(
    () =>
      connect((state: State) => ({
        user: state.auth.user,
        dropDownEntries,
        showNotificationIndicator: state.auth.user?.profileType === ProfileType.HealthcareProvider,
        soundService: SoundService,
        notificationService: NotificationService,
        topBarChildren: groupSelector,
      }))(DashboardLayout),
    [dropDownEntries, groupSelector]
  );

  return <ConnectedDashboard routes={routes}>{children}</ConnectedDashboard>;
});

export default DashboardWrapper;
